import { supabase } from '@/integrations/supabase/client';

/**
 * User from public.users table for assignment dropdown
 *
 * IMPORTANT: Contains BOTH ID types to support different use cases:
 * - id: auth_user_id for RPC calls (matches assign_user_to_conversation parameter)
 * - public_id: public.users.id for comparison with currentAssignedUserId
 */
export interface TenantUser {
  id: string;  // auth_user_id (auth.users.id) - for RPC calls
  public_id: string;  // public.users.id - for comparisons
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
}

/**
 * Fetch all active users (human agents) for a specific tenant
 *
 * Returns BOTH ID types:
 * - id: auth_user_id for passing to assign_user_to_conversation RPC
 * - public_id: public.users.id for comparing with currentAssignedUserId
 *
 * Why both? The RPC follows codebase pattern of accepting auth.users.id,
 * but conversations.assigned_user_id stores public.users.id (FK constraint).
 *
 * @param tenantId - UUID of the tenant
 * @returns List of active tenant users sorted by full name
 */
export async function getTenantAgents(tenantId: string) {
  const { data, error } = await supabase
    .from('users')
    .select('auth_user_id, id, full_name, email, role, is_active')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('full_name');

  if (error) {
    console.error('Error fetching tenant agents:', error);
    return { data: null, error };
  }

  // Map to TenantUser format with correct field names
  const tenantUsers = data?.map(user => ({
    id: user.auth_user_id,        // RPC expects auth.users.id
    public_id: user.id,            // For comparisons with currentAssignedUserId
    full_name: user.full_name,
    email: user.email,
    role: user.role,
    is_active: user.is_active
  }));

  return { data: tenantUsers as TenantUser[], error: null };
}

/**
 * Fetch a specific user by ID with tenant validation
 *
 * @param userId - UUID of the user
 * @param tenantId - UUID of the tenant (for validation)
 * @returns User data if found and belongs to tenant
 */
export async function getTenantUser(userId: string, tenantId: string) {
  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, email, role, is_active')
    .eq('id', userId)
    .eq('tenant_id', tenantId)
    .single();

  if (error) {
    console.error('Error fetching tenant user:', error);
    return { data: null, error };
  }

  return { data: data as TenantUser, error: null };
}
